import { useEffect, useState } from 'react';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { register } from '../services/authService';
import { getDevice } from '../services/deviceService';
import { useAuthStore } from '../store/useAuthStore';
import { useProfileStore } from '../store/useProfileStore';

/**
 * Full-page gate shown when an authenticated user has a half-finished profile
 * (missing first name, last name, or display name). Calls the same /register
 * endpoint the normal sign-up flow uses to finalize their account.
 *
 * Fields render conditionally so we never re-ask for data the profile already
 * has. Password is only requested for email-based accounts that have never had
 * one set (i.e. users who came in via the access-code path).
 */
export const RegistrationRequiredPage: React.FC = () => {
  const { t } = useTranslation('auth');
  const history = useHistory();
  const { search } = useLocation();
  const returnTo = new URLSearchParams(search).get('returnTo');

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const passwordSetAt = useAuthStore((s) => s.passwordSetAt);
  const activeProfile = useProfileStore((s) => s.activeProfile);

  const initialFirstName = activeProfile?.firstName?.trim() || '';
  const initialLastName = activeProfile?.lastName?.trim() || '';
  const initialDisplayName = activeProfile?.displayName?.trim() || '';

  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [displayNameDirty, setDisplayNameDirty] = useState(!!initialDisplayName);
  const [password, setPassword] = useState('');
  const [bio, setBio] = useState(activeProfile?.bio ?? '');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!displayNameDirty) {
      setDisplayName(`${firstName} ${lastName}`.trim());
    }
  }, [firstName, lastName, displayNameDirty]);

  // Optimistic-paint guard. RegistrationGate sent us here based on cached
  // `isRegistered=false`, but `activeProfile` may not be populated yet
  // (whoami still in flight). Don't redirect during render — that races
  // with the gate and Chrome will detect a navigation loop. Wait for the
  // profile to settle; either it arrives and we render the form, or
  // RegistrationGate's writeback flips `isRegistered=true` and bounces us
  // back to `/` on its own.
  if (!isAuthenticated) return <Redirect to="/" />;
  if (!activeProfile) {
    return (
      <IonPage>
        <IonContent>
          <div className="flex h-screen items-center justify-center bg-background">
            <IonSpinner name="crescent" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const needsFirstName = !initialFirstName;
  const needsLastName = !initialLastName;
  const needsDisplayName = !initialDisplayName;
  const hasEmail = !!activeProfile.email;
  const needsPassword = passwordSetAt == null && hasEmail;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!firstName.trim() || !lastName.trim() || !displayName.trim()) {
      setError(t('registrationRequired.errorRequired'));
      return;
    }
    if (needsPassword && !password) {
      setError(t('registrationRequired.errorPasswordRequired'));
      return;
    }
    if (!agreedToTerms) {
      setError(t('registrationRequired.errorTerms'));
      return;
    }

    setSubmitting(true);
    try {
      await register(activeProfile.userId, activeProfile.id, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        displayName: displayName.trim(),
        password: needsPassword ? password : undefined,
        ...(bio.trim() ? { bio: bio.trim() } : {}),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        identifier: getDevice()?.identifier || '',
        termAck: agreedToTerms,
      });
      history.replace(returnTo || '/');
    } catch {
      setError(t('registrationRequired.errorSave'));
      setSubmitting(false);
    }
  };

  return (
    <IonPage>
      <IonContent>
        <div className="mx-auto flex min-h-full w-full max-w-md flex-col justify-center gap-6 px-6 py-10">
          <img
            src="/images/HomePlatedTM.svg"
            alt="HomePlated"
            className="mx-auto"
            style={{ width: '300px' }}
          />
          <div className="space-y-2 text-center">
            <h1 className="text-2xl font-bold">{t('registrationRequired.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('registrationRequired.body')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {needsFirstName && (
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium mb-1">
                  {t('fields.firstName')}
                </label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  autoComplete="given-name"
                  className="w-full rounded-xl border border-border bg-background px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-primary"
                />
              </div>
            )}

            {needsLastName && (
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium mb-1">
                  {t('fields.lastName')}
                </label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  autoComplete="family-name"
                  className="w-full rounded-xl border border-border bg-background px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-primary"
                />
              </div>
            )}

            {needsDisplayName && (
              <div>
                <label htmlFor="displayName" className="block text-sm font-medium mb-1">
                  {t('fields.displayName')}
                </label>
                <input
                  id="displayName"
                  type="text"
                  value={displayName}
                  onChange={(e) => {
                    setDisplayName(e.target.value);
                    setDisplayNameDirty(true);
                  }}
                  required
                  className="w-full rounded-xl border border-border bg-background px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-primary"
                />
              </div>
            )}

            {needsPassword && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium mb-1">
                  {t('fields.passwordChoose')}
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="w-full rounded-xl border border-border bg-background px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-primary"
                />
              </div>
            )}

            <div>
              <label htmlFor="bio" className="block text-sm font-medium mb-1">
                {t('fields.bio')} <span className="text-muted-foreground font-normal">{t('fields.bioOptionalSuffix')}</span>
              </label>
              <textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                maxLength={500}
                rows={3}
                placeholder={t('fields.bioPlaceholder')}
                className="w-full resize-none rounded-xl border border-border bg-background px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
              <p className="mt-1 text-right text-xs text-muted-foreground">{bio.length}/500</p>
            </div>

            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                required
                className="mt-0.5 h-4 w-4 rounded border-border text-brand-primary focus:ring-brand-primary"
              />
              <span className="text-sm text-muted-foreground">
                {t('terms.agreePrefix')}{' '}
                <a
                  href="https://www.homeplated.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-primary hover:underline"
                >
                  {t('terms.linkText')}
                </a>
              </span>
            </label>

            {error && <p className="text-center text-sm text-brand-danger">{error}</p>}

            <button
              type="submit"
              disabled={submitting || !agreedToTerms}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-brand-primary py-3 text-base font-semibold text-white active:opacity-80 disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <IonSpinner name="crescent" className="h-5 w-5" />
                  {t('registrationRequired.submitting')}
                </>
              ) : (
                t('common:actions.continue')
              )}
            </button>
          </form>
        </div>
      </IonContent>
    </IonPage>
  );
};
